/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useMemo, useRef, useEffect} from 'react'
import {Link} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {Spinner} from 'react-bootstrap'
import useAuth from '../../../../hooks/useAuth'
import axios, {AxiosError} from 'axios'
import {RegisterUserRequest} from '../../../../redux/reducers/auth/types'
import useEstablishements from '../../../../hooks/useEstablishements'
import useClasses from '../../../../hooks/useClasses'
import useSubjects from '../../../../hooks/useSubjects'
import {AuthFullScreen} from './AuthFullScreen'
import TokenVerification, {submitTokenFormProps} from './RegistrationSteps/TokenVerification'
import GeneralInformation, {
  submitGeneralInformationFormProps,
} from './RegistrationSteps/GeneralInformation'
import Completed from './RegistrationSteps/Completed'
import ClassInformation, {
  submitClassInformationFormProps,
} from './RegistrationSteps/ClassInformation'
import PersonalInformation, {
  submitPersonalInformationFormProps,
} from './RegistrationSteps/PersonalInformation'
import {resolveRoleIndex} from '../../../../roles'

const STEPS = [
  {key: 'token', titleKey: 'MENU.TOKEN_VERIFICATION'},
  {key: 'general', titleKey: 'MENU.GENERAL_INFORMATION'},
  {key: 'personal', titleKey: 'MENU.PERSONAL_INFORMATION'},
  {key: 'class', titleKey: 'MENU.CLASS_INFORMATION'},
  {key: 'completed', titleKey: 'MENU.COMPLETED'},
]

export function Registration() {
  const {fetchAllEstablishements} = useEstablishements()
  const {fetchAllClasses} = useClasses()
  const {fetchAllSubjects} = useSubjects()

  useEffect(() => {
    fetchAllClasses({isFinished: false})
    fetchAllEstablishements({})
    fetchAllSubjects({})
  }, [])

  const intl = useIntl()
  const [isLoading, setIsLoading] = useState(false)
  const {actionLoading} = useAuth()
  const setLoading = (value: boolean) => setIsLoading(value)

  const [step, setStep] = useState(1)
  const {lastStep} = useMemo(() => ({initStep: 1, lastStep: 5}), [])

  const nextStep = () => setStep((s) => s + 1)
  const prevStep = () => setStep((s) => s - 1)

  const initUser: RegisterUserRequest = {
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    birthDay: '',
    placeOfBirth: '',
    location: '',
    governorate: '',
    zipCode: '',
    phone: '',
    classrooms: [],
    establishements: [],
    subjects: [],
    token: '',
    password: '',
    gender: '',
    identifiantStudent: '',
  }

  const [data, setData] = useState<RegisterUserRequest>(initUser)
  const [error, setError] = useState('')
  const submitBtnRef = useRef<HTMLButtonElement>(null)

  const onSubmitToken = ({token, role, identifiantStudent}: submitTokenFormProps) => {
    setData((d) => ({
      ...d,
      token,
      role,
      ...(identifiantStudent ? {identifiantStudent} : {}),
    }))
    nextStep()
  }

  const onSubmitGeneralInformation = (values: submitGeneralInformationFormProps) => {
    setData((d) => ({...d, ...values}))
    nextStep()
  }

  const onSubmitPersonalInformation = (values: submitPersonalInformationFormProps) => {
    setData((d) => ({...d, ...values}))
    nextStep()
  }

  const onSubmitClassInformation = async (values: submitClassInformationFormProps) => {
    try {
      const request: RegisterUserRequest = {...data, ...values}
      const roleIdx = resolveRoleIndex(request.role)
      if (roleIdx !== 4 && roleIdx !== 8) {
        delete request.identifiantStudent
      } else if (!request.identifiantStudent?.trim()) {
        delete request.identifiantStudent
      }
      setData(request)
      const API_URL = process.env.REACT_APP_API_URL
      setLoading(true)
      await axios.post(`${API_URL}/api/auth/register`, request)
      setLoading(false)
      setError('')
      nextStep()
    } catch (err) {
      const e = err as AxiosError
      const message: any = e.response?.data
      setError(message[intl.locale] ?? '')
      setLoading(false)
    }
  }

  const onSubmitClick = () => {
    submitBtnRef.current?.click()
  }

  const stepContent = (
    <>
      {step === 1 && (
        <TokenVerification
          user={data}
          onSubmit={onSubmitToken}
          btnRef={submitBtnRef}
          setLoading={setLoading}
        />
      )}
      {step === 2 && (
        <GeneralInformation
          user={data}
          onSubmit={onSubmitGeneralInformation}
          btnRef={submitBtnRef}
          setLoading={setLoading}
        />
      )}
      {step === 3 && (
        <PersonalInformation
          onSubmit={onSubmitPersonalInformation}
          btnRef={submitBtnRef}
          user={data}
        />
      )}
      {step === 4 && (
        <ClassInformation
          onSubmit={onSubmitClassInformation}
          btnRef={submitBtnRef}
          user={data}
          error={error}
        />
      )}
      {step === 5 && <Completed email={data.email} />}
    </>
  )

  const submitting = isLoading || actionLoading

  return (
    <AuthFullScreen>
      <div className='marefati-signup'>
          <div className='marefati-signup__header'>
            <div className='marefati-login__secure'>
              <span className='marefati-login__lock'>
                <i className='bi bi-shield-lock-fill' />
              </span>
              <span className='marefati-login__secure-text'>Espace sécurisé Maarefati</span>
            </div>
          </div>

          <div className='marefati-signup__title'>
            <h1>{intl.formatMessage({id: 'MENU.CREATE_AN_ACCOUNT'})}</h1>
            <p>Suivre, Comprendre, Réussir</p>
          </div>

          <nav className='marefati-stepper' aria-label='Registration steps'>
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`marefati-stepper__item ${
                  step === i + 1 ? 'marefati-stepper__item--current' : ''
                } ${step > i + 1 ? 'marefati-stepper__item--done' : ''}`}
              >
                <div className='marefati-stepper__indicator'>
                  {step > i + 1 ? (
                    <i className='bi bi-check-lg' />
                  ) : (
                    <span className='marefati-stepper__num'>{i + 1}</span>
                  )}
                </div>
                <span className='marefati-stepper__label'>
                  {intl.formatMessage({id: s.titleKey})}
                </span>
                {i < STEPS.length - 1 && <div className='marefati-stepper__line' />}
              </div>
            ))}
          </nav>

          <div className='marefati-signup__content'>
            {stepContent}
          </div>

          {step < lastStep && (
            <div className='marefati-signup__actions'>
              {submitting ? (
                <button type='button' className='marefati-login__submit' disabled>
                  <Spinner role='status' animation='border' size='sm' className='me-2' />
                  {intl.formatMessage({id: 'MENU.CONTINUE'})}
                </button>
              ) : (
                <>
                  {step > 1 && step < 5 && (
                    <button
                      type='button'
                      onClick={prevStep}
                      className='marefati-login__secondary-btn'
                    >
                      <i className='bi bi-arrow-left me-1' />
                      {intl.formatMessage({id: 'COMMON.BACK'})}
                    </button>
                  )}
                  <button
                    type='button'
                    onClick={onSubmitClick}
                    className='marefati-login__submit'
                  >
                    {step < 4
                      ? intl.formatMessage({id: 'MENU.CONTINUE'}) + ' '
                      : intl.formatMessage({id: 'MENU.SUBMIT'}) + ' '}
                    <i className='bi bi-arrow-right ms-1' />
                  </button>
                </>
              )}
            </div>
          )}

          <div className='marefati-login__meta'>
            <span className='marefati-login__version'>v1.0.0</span>
            <span className='marefati-login__copy'>
              © {new Date().getFullYear()} Maarefati
            </span>
          </div>

          <div className='marefati-signup__login'>
            <span className='marefati-login__signup-text'>
              {intl.formatMessage({id: 'AUTH.ALREADY_HAVE_ACCOUNT'})}{' '}
            </span>
            <Link to='/auth/login' className='marefati-login__signup-btn'>
              {intl.formatMessage({id: 'MENU.LOGIN'})}
            </Link>
          </div>
        </div>
    </AuthFullScreen>
  )
}
